import { FieldConfig } from "@/types/fieldConfig";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Selection,
    Divider,
    Chip,
} from "@heroui/react";
import { useMemo, useState } from "react";
import useRequest from "@/lib/request/useRequest";
import {
    ApplicationPlan,
    PlantingPlan,
    SimulatedCropPlan,
} from "@/types/cropPlan";
import { Organization } from "@/types/organization";
import { CropConfiguration } from "@/types/cropConfiguration";
import { NitrogenSimulationType } from "@/types/enums";
import allNHorizontalImage from "@/assets/images/all_n_horizontal.png";
import { PlantingPlanForm } from "./PlantingPlanForm";
import { ApplicationPlanForm } from "./ApplicationPlanForm";
import { ConfirmPrimaryPlan } from "./ConfirmPlan";


function BulkNPlanner({
    fields,
    onClose,
}: {
    fields: FieldConfig[];
    onClose?: () => void;
}) {
    const { useGetRequest } = useRequest();

    const cropsQuery = useGetRequest<CropConfiguration[]>({
        url: `/crops/`,
    });

    const organizationsQuery = useGetRequest<Organization[]>({
        url: `/organizations/`,
    });

    const organizationOptions = useMemo(() => {
        if (!organizationsQuery.data) {
            return [];
        }
        return organizationsQuery.data.map((org) => ({
            description: org.name,
            id: org.id,
        }));
    }, [organizationsQuery.data]);

    const cropPlansRequest = useGetRequest<SimulatedCropPlan[]>({
        url: `/simulated_crop_plans/`,
    });

    const plantingPlansRequest = useGetRequest<PlantingPlan[]>({
        url: `/planting_plans/`,
    });

    const applicationPlansRequest = useGetRequest<ApplicationPlan[]>({
        url: `/application_plans/`,
    });

    const [selectedPlantingPlanId, setSelectedPlantingPlanId] =
        useState<Selection>(new Set([]));

    const plantingPlan = useMemo<PlantingPlan | null>(() => {
        const selectedId = Number(Array.from(selectedPlantingPlanId)[0]);
        return (
            plantingPlansRequest.data?.find((plan => plan.id === selectedId)) || null
        );
    }, [plantingPlansRequest.data, selectedPlantingPlanId]);

    const cropStageOptions = useMemo(() => {
        if (!cropsQuery.data || !plantingPlan?.crop_configuration) {
            return [];
        }
        const selectedCrop = cropsQuery.data.find(
            (crop) => crop.id === plantingPlan.crop_configuration,
        );
        if (selectedCrop) {
            return selectedCrop.stages.map((stage) => ({
                id: stage.id,
                description: stage.growth_stage,
            }));
        }

        return [];
    }, [cropsQuery.data, plantingPlan]);

    const [selectedApplicationPlanIds, setSelectedApplicationPlanIds] =
        useState<Selection>(new Set([]));

    const applicationPlan = useMemo(() => {
        const applicationPlanId = Number(Array.from(selectedApplicationPlanIds)[
            Array.from(selectedApplicationPlanIds).length - 1
        ]);
        return applicationPlansRequest.data?.find(
            (plan) => plan.id === applicationPlanId,
        );
    }, [applicationPlansRequest.data, selectedApplicationPlanIds]);

    const selectedSimulations = new Set([NitrogenSimulationType.Sentinel]);

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            className="max-w-8xl h-[90vh]"
            scrollBehavior="inside"
        >
            <ModalContent>
                <ModalHeader>
                    <div className="flex flex-row items-center w-full justify-center gap-4 font-bold text-3xl text-primary-500 ml-4">
                        <img
                            src={allNHorizontalImage}
                            alt="All N Horizontal"
                            className="h-10 object-cover"
                        />
                        Planner
                    </div>
                </ModalHeader>
                <ModalBody className="mx-4">
                    <PlantingPlanForm
                        organizations={organizationOptions}
                        selectedPlantingPlanId={selectedPlantingPlanId}
                        setSelectedPlantingPlanId={
                            setSelectedPlantingPlanId
                        }
                        plantingPlansRequest={plantingPlansRequest}
                        cropsQuery={cropsQuery}
                    />
                    <Divider className="my-3" />
                    <ApplicationPlanForm
                        fields={fields}
                        organizations={organizationOptions}
                        selectedApplicationPlanIds={selectedApplicationPlanIds}
                        setSelectedApplicationPlanIds={setSelectedApplicationPlanIds}
                        applicationPlansRequest={applicationPlansRequest}
                        cropStageOptions={cropStageOptions}
                        plantingPlan={plantingPlan!}
                        cropPlansRequest={cropPlansRequest}
                        selectedSimulations={selectedSimulations}
                        selectionMode="single"
                        setPrimaryPlan={false}
                    />
                </ModalBody>
                <ModalFooter>
                    <div className="flex flex-row gap-3 items-center w-full">
                        <div className="flex flex-row w-full items-center gap-1 font-semibold text-xl text-primary-500 overflow-x-auto">
                            Fields:
                            {fields.map((field) => (
                                <Chip key={field.id} color="primary" variant="flat">
                                    {field.name}
                                </Chip>
                            ))}
                        </div>
                        <div className="flex flex-row gap-1 mx-3 justify-end">
                            {(Array.from(selectedPlantingPlanId).length === 0 ||
                                Array.from(selectedApplicationPlanIds).length ===
                                0) && (
                                    <div className="flex flex-row items-center gap-2">
                                        {Array.from(selectedPlantingPlanId).length ===
                                            0 && (
                                                <Chip color="warning" variant="flat">
                                                    Select a Planting Plan
                                                </Chip>
                                            )}
                                        {Array.from(selectedApplicationPlanIds).length ===
                                            0 && (
                                                <Chip color="warning" variant="flat">
                                                    Select an Application Plan
                                                </Chip>
                                            )}
                                    </div>
                                )}
                            <ConfirmPrimaryPlan
                                fields={fields}
                                plantingPlan={plantingPlan!}
                                applicationPlan={applicationPlan!}
                                cropPlans={cropPlansRequest.data}
                                onSave={onClose}
                            />
                        </div>
                    </div>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default BulkNPlanner;